import React, { useEffect } from 'react'
import styled from '@emotion/styled'
import { Colors, Levels, PRINT_HIDE, Shapes } from 'src/components/theme'
import { Typography } from '@mui/material'
import { MapStyle } from 'src/components/map-new/types'
import Image from 'next/image'

import RoadImage from 'src/assets/images/map-type-road.png'
import SatelliteImage from 'src/assets/images/map-type-satellite.png'
import DarkImage from 'src/assets/images/map-type-dark.jpg'
import LightImage from 'src/assets/images/map-type-light.jpg'
import ExpImage from 'src/assets/images/map-type-exp.png'
import { isFeatureEnabled } from 'src/utils/feature-flag'
import { MAP_DEFAULT_STYLE } from 'src/components/map/mapbox/config'

type StyleToggleProps = {
  mapStyle: MapStyle
  onMapStyleChange: (mapStyle: MapStyle) => void
}

export const MapTypeToggleWrapper = styled.div`
  position: relative;
  background-color: ${Colors.bgWhite};
  padding: 4px;
  cursor: pointer;
  transition: right 1s ease-in-out;
  z-index: ${Levels.mapTypeToggle};
  border-radius: 4px;
  width: 64px;
  height: 64px;
  box-shadow: rgb(0 0 0 / 30%) 0px 1px 4px -1px;
  ${PRINT_HIDE};
`

export const MapTypeToggleContent = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 2px;

  & > img {
    position: absolute !important;
    top: 50% !important;
    left: 50% !important;
    width: 86px !important;
    height: 86px !important;
    -webkit-transform: translateY(-50%) translateX(-50%) !important;
    transform: translateY(-50%) translateX(-50%) !important;
  }
`

export const MapTypeToggleContentOverlay = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(0deg, rgba(7, 44, 45, 0.5) 0%, rgba(7, 44, 45, 0) 100%);
`

export const MapTypeToggleTitle = styled.div`
  position: absolute;
  bottom: ${Shapes.spacing[1]}px;
  left: ${Shapes.spacing[1]}px;
`

const MapTypeToggleMenu = styled.div`
  position: absolute;
  bottom: 0px;
  right: 0;
  width: 64px;
  background: ${Colors.bgWhite};
  padding: 0 ${Shapes.spacing[1]}px;
  border-radius: 4px;
  box-shadow: rgb(0 0 0 / 30%) 0px 1px 4px -1px;
  z-index: ${Levels.appMenu};
`

const MapTypeToggleMenuItem = styled.div`
  position: relative;
  width: 56px;
  height: 56px;
  margin: ${Shapes.spacing[1]}px 0;

  & > img {
    width: 56px;
    height: 56px;
  }

  & > p {
    position: absolute;
    bottom: ${Shapes.spacing[1]}px;
    left: ${Shapes.spacing[1]}px;
  }
`

const STYLES_ORDER = [MapStyle.STREETS, MapStyle.SATELLITE, MapStyle.LIGHT, MapStyle.DARK]
const STYLES_IMAGES = [RoadImage, SatelliteImage, LightImage, DarkImage]
const STYLES_NAMES = ['Map', 'Satellite', 'Light', 'Dark']
const STYLES_COLORS = [Colors.bgWhite, Colors.bgWhite, Colors.bgWhite, Colors.bgWhite]

const STYLES_EXPERIMENTAL = [MapStyle.THREEDSATELLITE, MapStyle.EXP1, MapStyle.EXP2, MapStyle.EXP3, MapStyle.MAPBOX]
const STYLES_EXPERIMENTAL_IMAGES = [SatelliteImage, ExpImage, ExpImage, ExpImage, RoadImage]
const STYLES_EXPERIMENTAL_NAMES = ['3D', 'Exp 1', 'Exp 2', 'Exp 3', 'Mapbox']
const STYLES_EXPERIMENTAL_COLORS = [Colors.bgWhite, Colors.bgWhite, Colors.bgWhite, Colors.bgWhite, Colors.bgWhite]

export const StyleToggle = (props: StyleToggleProps) => {
  const { mapStyle, onMapStyleChange } = props
  const [isMenuOpen, setIsMenuOpen] = React.useState(false)
  const isExperimentalMaps = isFeatureEnabled('map-experimental-styles')

  const ORDER = isExperimentalMaps ? STYLES_ORDER.concat(STYLES_EXPERIMENTAL) : STYLES_ORDER
  const IMAGES = isExperimentalMaps ? STYLES_IMAGES.concat(STYLES_EXPERIMENTAL_IMAGES) : STYLES_IMAGES
  const NAMES = isExperimentalMaps ? STYLES_NAMES.concat(STYLES_EXPERIMENTAL_NAMES) : STYLES_NAMES
  const COLORS = isExperimentalMaps ? STYLES_COLORS.concat(STYLES_EXPERIMENTAL_COLORS) : STYLES_COLORS

  const onClick = () => {
    setIsMenuOpen(!isMenuOpen)
  }

  const onSelectClick = (style: MapStyle) => () => {
    onMapStyleChange(style)
    setIsMenuOpen(false)
  }

  useEffect(() => {
    // Prevent invalid map style for local storage poisoning
    if (ORDER.indexOf(mapStyle) === -1) {
      onMapStyleChange(MAP_DEFAULT_STYLE)
      setIsMenuOpen(false)
    }
  }, [mapStyle])

  const index = ORDER.indexOf(mapStyle)
  const img = IMAGES[index]
  const title = NAMES[index]
  const color = COLORS[index]

  return (
    <MapTypeToggleWrapper onClick={onClick}>
      <MapTypeToggleContent>
        <Image src={img} alt={title} layout="fill" />
        <MapTypeToggleContentOverlay />
        <MapTypeToggleTitle>
          <Typography variant="text2m" color={color}>
            {title}
          </Typography>
        </MapTypeToggleTitle>
      </MapTypeToggleContent>
      {isMenuOpen && (
        <MapTypeToggleMenu>
          {ORDER.map((style, index) => {
            return (
              <MapTypeToggleMenuItem key={style} onClick={onSelectClick(style)}>
                <Image src={IMAGES[index]} alt={NAMES[index]} />
                <MapTypeToggleContentOverlay />
                <Typography variant="text2m" color={COLORS[index]}>
                  {NAMES[index]}
                </Typography>
              </MapTypeToggleMenuItem>
            )
          })}
        </MapTypeToggleMenu>
      )}
    </MapTypeToggleWrapper>
  )
}
